import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { z } from 'zod';
import { useAuthStore } from '@/lib/auth/authStore';

/**
 * Merch planner — las entradas, guardadas en el dispositivo por marca y evento.
 * Se guardan los textos tecleados (no números); los porcentajes, en %. Las casillas
 * no se guardan. Son de la sesión: se borran al cerrarla y no se leen si el dispositivo
 * los guardó para otro usuario. Sólo números tecleados: sin datos personales.
 */
export type DraftField =
  | 'events'
  | 'guests'
  | 'shareWhoPlay'
  | 'claimRate'
  | 'buffer'
  | 'hours'
  | 'minutesPerHandout'
  | 'stations';
export type MerchPlanDraft = Record<DraftField, string>;

/** Con evento, `events`, `stations` y `hours` no se usan del defecto (vienen del evento). */
export const DEFAULT_DRAFT: MerchPlanDraft = {
  events: '1',
  guests: '',
  shareWhoPlay: '40',
  claimRate: '90',
  buffer: '15',
  hours: '3',
  minutesPerHandout: '2',
  stations: '1',
};

const field = z.string().max(16);
export const merchPlanDraftSchema = z.object({
  events: field.optional(),
  guests: field.optional(),
  shareWhoPlay: field.optional(),
  claimRate: field.optional(),
  buffer: field.optional(),
  hours: field.optional(),
  minutesPerHandout: field.optional(),
  stations: field.optional(),
  savedAt: z.number(),
});
type StoredDraft = z.infer<typeof merchPlanDraftSchema>;
const PersistedSchema = z.object({ owner: z.number().nullable(), drafts: z.record(z.string(), merchPlanDraftSchema) });

const MAX_DRAFTS = 30;
const currentUserId = (): number | null => useAuthStore.getState().user?.id ?? null;

/** Clave del borrador: `<brandId>:<eventId|none>`. */
export const draftKey = (brandId: number, eventId: number | null): string => `${brandId}:${eventId ?? 'none'}`;

/** El borrador guardado con los valores por defecto donde falte algo. */
export function withDefaults(d: StoredDraft | undefined): MerchPlanDraft {
  return { ...DEFAULT_DRAFT, ...(d ?? {}), guests: d?.guests ?? DEFAULT_DRAFT.guests };
}

interface MerchPlanDraftsState {
  /** Usuario que tecleó los borradores. */
  owner: number | null;
  drafts: Record<string, StoredDraft>;
  setField: (key: string, name: DraftField, value: string) => void;
}

export const useMerchPlanDrafts = create<MerchPlanDraftsState>()(
  persist(
    (set) => ({
      owner: null,
      drafts: {},
      setField: (key, name, value) =>
        set((s) => {
          const next = { ...s.drafts, [key]: { ...(s.drafts[key] ?? {}), [name]: value, savedAt: Date.now() } };
          const keys = Object.keys(next);
          if (keys.length > MAX_DRAFTS) {
            keys
              .sort((a, b) => next[b].savedAt - next[a].savedAt)
              .slice(MAX_DRAFTS)
              .forEach((k) => delete next[k]);
          }
          return { owner: currentUserId(), drafts: next };
        }),
    }),
    {
      name: 'xotski.merchPlanner.drafts',
      version: 1,
      partialize: (s) => ({ owner: s.owner, drafts: s.drafts }),
      // Un borrador a mano, corrupto o de otro usuario se descarta entero: valores por defecto.
      merge: (persisted, current) => {
        const ok = PersistedSchema.safeParse(persisted);
        const mine = ok.success && ok.data.owner != null && ok.data.owner === currentUserId();
        return mine ? { ...current, owner: ok.data.owner, drafts: ok.data.drafts } : current;
      },
    },
  ),
);

// Los valores tecleados en la calculadora vieja cambiaron de significado (asistencia por día →
// invitados por evento): no se migran, y se borra la clave para que no sobreviva al cierre de sesión.
try {
  localStorage.removeItem('xotski.calculator.drafts');
} catch {
  /* sin almacenamiento */
}

// Al cerrar sesión (o entrar otro usuario) los borradores se borran, también
// del almacenamiento. Sin tocar authStore: la suscripción vive aquí.
useAuthStore.subscribe((auth, prev) => {
  const owner = useMerchPlanDrafts.getState().owner;
  const gone = prev.token != null && auth.token == null;
  const other = owner != null && auth.user?.id != null && auth.user.id !== owner;
  if (!gone && !other) return;
  useMerchPlanDrafts.setState({ owner: null, drafts: {} });
  useMerchPlanDrafts.persist.clearStorage();
});
